import React, { useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { VariantProvider, isReviewHost, useVariant, type VariantKey } from './variant';
import * as B from './variants/b';
import * as C from './variants/c';
import { SharedNav, SharedFooter } from './ui/chrome';
import Platform from './pages/Platform';
import PlatformDetail from './pages/PlatformDetail';
import Industries from './pages/Industries';
import Industry from './pages/Industry';
import Integrations from './pages/Integrations';
import ConnectorPage from './pages/Connector';
import WhyTrust from './pages/WhyTrust';
import Customers from './pages/Customers';
import Pricing from './pages/Pricing';
import NotFound from './pages/NotFound';
import BookDemo from './booking/BookDemo';
import Tour from './tour/Tour';
import GapPreview from './story/Preview';
import WhyPage from './why/WhyPage';
import IntelligencePage from './ai/IntelligencePage';
import ExplainerGallery from './explainers/GalleryPage';
import Moodboard from './review/Moodboard';

type VariantModule = { Home: React.ComponentType; Nav?: React.ComponentType; Footer?: React.ComponentType };
const MODULES: Record<VariantKey, VariantModule> = { b: B, c: C };

// New page: start at the top (or at the #hash). Reload: go back to where the reader was, so a refresh
// (or a dev rebuild) never throws them to the top. The SPA renders after load, so the browser's own
// restoration can't do this; we keep the position per path in sessionStorage.
const SCROLL_KEY = (path: string) => `bm-scroll:${path}`;
const isReload = () => {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === 'reload';
  } catch {
    return false;
  }
};

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const last = useRef<string | null>(null);

  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    let t = 0;
    const save = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => {
        try {
          sessionStorage.setItem(SCROLL_KEY(location.pathname), String(Math.round(window.scrollY)));
        } catch {
          // storage unavailable
        }
      }, 120);
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, []);

  useEffect(() => {
    const key = pathname + hash;
    if (last.current === key) return; // same page (dev double-run, query change): leave the scroll alone
    const firstRun = last.current === null;
    last.current = key;
    if (firstRun) {
      let saved = 0;
      try {
        saved = Number(sessionStorage.getItem(SCROLL_KEY(pathname)) || 0);
      } catch {
        // storage unavailable
      }
      if (isReload() && saved > 0) {
        // wait for images and sections to give the page its height, then restore
        let tries = 0;
        const go = () => {
          const room = document.documentElement.scrollHeight - window.innerHeight;
          if (room >= saved || tries++ > 40) window.scrollTo(0, Math.min(saved, room));
          else window.setTimeout(go, 50);
        };
        go();
        return;
      }
    }
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

/** Review-only pages: 404 in public, never indexed. */
function ReviewOnly({ children }: { children: React.ReactElement }) {
  const ok = isReviewHost();
  useEffect(() => {
    if (!ok) return;
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex, nofollow';
    document.head.appendChild(m);
    return () => m.remove();
  }, [ok]);
  return ok ? children : <NotFound />;
}

function Shell() {
  const { variant } = useVariant();
  const mod = MODULES[variant];
  const Nav = mod.Nav ?? SharedNav;
  const Footer = mod.Footer ?? SharedFooter;
  const Home = mod.Home;

  return (
    <div className="v2 min-h-screen" data-variant={variant}>
      <ScrollToTop />
      <Nav />
      <main id="main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/platform/ai" element={<IntelligencePage />} />
          <Route path="/platform/:slug" element={<PlatformDetail />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/:slug" element={<Industry />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/integrations/:slug" element={<ConnectorPage />} />
          <Route path="/why-trust-us" element={<WhyTrust />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/book-a-demo" element={<BookDemo />} />
          <Route path="/why" element={<WhyPage />} />
          <Route path="/tour" element={<Tour />} />
          <Route path="/__moodboard" element={<ReviewOnly><Moodboard /></ReviewOnly>} />
          <Route path="/__gap" element={<ReviewOnly><GapPreview /></ReviewOnly>} />
          <Route path="/__explainers" element={<ReviewOnly><ExplainerGallery /></ReviewOnly>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

export default function Site() {
  return (
    <VariantProvider>
      <Shell />
    </VariantProvider>
  );
}
